import axios from 'axios';
import getAuthSession from './auth';
import { DataGridQueryParams, buildDataGridParams } from '../utils/adminDashboardUtils';
import { QCAdminResponse, QCReview, QCRun } from '../interfaces/qcReview.interface';
import { QCReviewItem } from '../interfaces/qcReviewData.interface';
import {
  AdminQcIssueRow,
  AdminQcRunSummary,
  QcIssue,
  QcIssueCategory,
  QcIssueReply,
  QcIssueStatus,
  QcPreviewResult,
  QcPreviewScope,
  QcReviewCycleState,
  WorkflowQcComment,
} from '../interfaces/qcIssue.interface';

// GET /qc/workflows/<id>/runs/ (QCWorkflowRunsAPIView) — every run with pipeline stats + per-question verdicts.
export async function getWorkflowQcRunSummaries(workflowId: number): Promise<AdminQcRunSummary[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/runs/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as AdminQcRunSummary[];
}

// POST /qc/workflows/<workflow_id>/run/ (qc/views.py: RunQCAPIView). Fire-and-forget by
// design (handoff §3) — the backend enqueues the run and emails the triggering admin when
// it's ready; the UI never polls QCRun.status.
export async function triggerAssessmentQC(workflowId: number): Promise<QCRun> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/run/`,
    {},
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCRun;
}

// GET /qc/workflows/<workflow_id>/reviews/ (qc/views.py: QCReviewListAPIView). Returns the
// current QCReview rows for the workflow's assessment survey as a plain array (no pagination).
export async function getQCReviews(workflowId: number): Promise<QCReview[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/reviews/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReview[];
}

// GET /qc/workflows/<workflow_id>/review-data/ (qc/views.py: AssessmentQCReviewDataAPIView).
// One fully-resolved bundle per NI/PI AssessorAnswer — question + sub-controls, SP
// disposition, evidence, comment thread — replacing the fetchAssessment/fetchSurvey/
// fetchDetailedQuestionnaire + per-item evidence/comment fetch chain the review page used.
export async function getQCReviewData(workflowId: number): Promise<QCReviewItem[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/review-data/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReviewItem[];
}

// PATCH /qc/reviews/<qc_review_id>/admin-response/ (qc/views.py: QCReviewAdminResponseAPIView).
// Records the admin's accept/edit/reject reaction to ONE QC verdict - the ADR-0005
// agreement-rate signal. Orthogonal to AssessorAnswer.verification_status (see
// updateAssessorAnswer in services/assessments.ts) - never set both from one control
// (ADR-0007). admin_response_by/admin_response_at are stamped server-side, not sent here.
export async function updateQCReviewAdminResponse(
  qcReviewId: number,
  adminResponse: QCAdminResponse,
  adminResponseNote?: string,
): Promise<QCReview> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/reviews/${qcReviewId}/admin-response/`,
    { adminResponse, adminResponseNote: adminResponseNote || '' },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReview;
}

// POST /qc/issues/<id>/accept/ — publishes a draft as pending_assessor_action under the accepting admin; rating 0 = unrated → null.
export async function acceptQcIssueDraft(
  qcIssueId: number,
  feedback: { rating: number; comment: string },
): Promise<QcIssue> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${qcIssueId}/accept/`,
    { draftRating: feedback.rating || null, draftFeedback: feedback.comment },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue;
}

// POST /qc/issues/<id>/reject/ — terminal, admin-only (no trace on the assessment); reason required, rating/comment are training feedback.
export async function rejectQcIssueDraft(
  qcIssueId: number,
  rejection: { rating: number; reason: string; comment: string },
): Promise<QcIssue> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${qcIssueId}/reject/`,
    { draftRating: rejection.rating || null, rejectReason: rejection.reason, draftFeedback: rejection.comment },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue;
}

// GET /qc/workflows/<id>/issues/ — workspace contract; admins get drafts too, assessors published only.
export async function getWorkflowQcIssues(workflowId: number): Promise<QcIssue[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/issues/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue[];
}

// POST /qc/workflows/<id>/issues/ — manual admin issue; the server resolves the AssessorAnswer, sets origin/status, returns the persisted issue.
export async function createWorkflowQcIssue(
  workflowId: number,
  questionId: number,
  category: QcIssueCategory,
  text: string,
): Promise<QcIssue> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/issues/`,
    { question: questionId, category, text },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue;
}

// PATCH /qc/issues/<id>/ — edit an unpublished issue; published issues are immutable server-side.
export async function updateWorkflowQcIssue(
  issueId: number,
  category: QcIssueCategory,
  text: string,
): Promise<QcIssue> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${issueId}/`,
    { category, text },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue;
}

// DELETE /qc/issues/<id>/ — remove an unpublished manual issue.
export async function deleteWorkflowQcIssue(issueId: number): Promise<void> {
  const authSession = await getAuthSession();
  await axios.delete(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${issueId}/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
}

// POST /qc/issues/<id>/replies/ — admins reply anywhere; the assessor only post-publication and never on admin-only statuses.
export async function addQcIssueReply(qcIssueId: number, text: string): Promise<QcIssueReply> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${qcIssueId}/replies/`,
    { text },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssueReply;
}

// PATCH /qc/replies/<qc_reply_id>/ (qc/views.py: QCIssueReplyDetailAPIView) — author-only.
export async function updateQcIssueReply(qcReplyId: number, text: string): Promise<QcIssueReply> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/replies/${qcReplyId}/`,
    { text },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssueReply;
}

// DELETE /qc/replies/<qc_reply_id>/ — author-only.
export async function deleteQcIssueReply(qcReplyId: number): Promise<void> {
  const authSession = await getAuthSession();
  await axios.delete(
    `${process.env.REACT_APP_BASE_API}/qc/replies/${qcReplyId}/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
}

// GET /qc/workflows/<id>/review-cycle/ — null until Submit QC Issues creates the cycle.
export async function getWorkflowQcReviewCycle(workflowId: number): Promise<QcReviewCycleState | null> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/review-cycle/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcReviewCycleState | null;
}

// POST /qc/workflows/<id>/submit-qc-issues/ — the publication event: stamps the cycle, publishes, emails the assessor; rejected while unaddressed drafts remain.
export async function submitWorkflowQcIssues(workflowId: number): Promise<QcReviewCycleState> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/submit-qc-issues/`,
    {},
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcReviewCycleState;
}

// GET /qc/workflows/<id>/comments/ — the per-question general thread; TPN admins and the workflow's assessor only.
export async function getWorkflowQcComments(workflowId: number): Promise<WorkflowQcComment[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/comments/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as WorkflowQcComment[];
}

// POST /qc/workflows/<id>/comments/ — the server resolves the AssessorAnswer and stamps the author from the token.
export async function addWorkflowQcComment(
  workflowId: number,
  questionId: number,
  text: string,
): Promise<WorkflowQcComment> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/comments/`,
    { question: questionId, text },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as WorkflowQcComment;
}

// PATCH /qc/workflows/<id>/score/ — admin override, reason mandatory + audit-logged; score_submitted first, score_post_submission on later edits.
export async function updateWorkflowQcScore(
  workflowId: number,
  score: number,
  reason: string,
): Promise<{ effectiveScore: number | null }> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/score/`,
    { score, reason },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as { effectiveScore: number | null };
}

// PATCH /qc/workflows/<id>/points/ — the Point Manager tallies, also audit-logged.
export async function updateWorkflowQcPoints(
  workflowId: number,
  pointsDeducted: number,
  pointsTotal: number,
): Promise<{ pointsDeducted: number; pointsTotal: number }> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/points/`,
    { pointsDeducted, pointsTotal },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as { pointsDeducted: number; pointsTotal: number };
}

// PATCH /qc/issues/<id>/status/ — server enforces legal moves: assessor open → ready (post-publication); admin ready → approved/needs_update.
export async function updateQcIssueStatus(qcIssueId: number, status: QcIssueStatus): Promise<QcIssue> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/issues/${qcIssueId}/status/`,
    { status },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcIssue;
}

// POST /qc/workflows/<id>/approve-assessment/ — terminal event: needs every published issue approved; stamps the cycle, records the score, audit-logs, and emails the assessor with the notes.
export async function approveWorkflowAssessment(
  workflowId: number,
  approval: { finalScore: number; overrideReason: string; emailNotes: string },
): Promise<QcReviewCycleState> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/approve-assessment/`,
    approval,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcReviewCycleState;
}

// GET /qc/issues/ (QCIssueAdminListAPIView) — admin cross-assessment grid; server-mode DataGrid params (limit/offset + column-field ordering/filters). TPN admins only.
export async function getAdminQcIssuesForDataGrid(
  params: DataGridQueryParams,
): Promise<{ results: AdminQcIssueRow[]; count: number }> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/issues/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
      params: buildDataGridParams(params),
    },
  );
  return {
    results: response.data.results as AdminQcIssueRow[],
    count: response.data.count,
  };
}

// GET /qc/surveys/<id>/preview/ (QCPreviewScopeAPIView) — dry-run scope + config; survey-keyed since older completed assessments lack an ACTIVE workflow.
export async function getQcPreviewScope(assessmentSurveyId: number): Promise<QcPreviewScope> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/surveys/${assessmentSurveyId}/preview/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcPreviewScope;
}

// POST /qc/surveys/<id>/preview/questions/<assessorAnswerId>/ — evaluates one question, writes nothing; the client drives the loop.
export async function previewQcQuestion(
  assessmentSurveyId: number,
  assessorAnswerId: number,
): Promise<QcPreviewResult> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/surveys/${assessmentSurveyId}/preview/questions/${assessorAnswerId}/`,
    {},
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QcPreviewResult;
}
